/**
 * RendererDependencyLoader.js
 *
 * ES6 Module for loading and managing FullCalendar renderer and plugin dependencies.
 *
 * Responsibilities:
 * - Load and provide the FullCalendar renderer implementation
 * - Load and cache FullCalendar plugins dynamically
 * - Track and report dependency loading errors
 * - Clear caches and error history for testing or recovery
 *
 * Features:
 * - Async dynamic imports for renderer and plugins
 * - Caching to prevent redundant loads
 * - Minimal error tracking using ErrorHandler
 * - Named exports for flexibility and testability
 * - Error preservation: original errors are preserved via Error.cause
 *
 * Limitations:
 * - Assumes browser environment for dynamic imports
 * - Does not support server-side rendering
 * - No built-in support for circular dependencies
 */

import ErrorHandler from '../../utils/ErrorHandler.js';

// Essential error tracking setup
const errorHandler = new ErrorHandler({
  module: 'RendererDependencyLoader',
  maxErrors: 5
});

/**
 * Extract the default export from an ES module.
 * ES modules may export via `default` or as a namespace object.
 * This helper provides consistent access to the primary export.
 *
 * @param {Object} module - The imported module object
 * @returns {*} The default export or the module itself
 * @private
 */
function extractDefaultExport(module) {
  // Prefer explicit default export if available
  if (module && typeof module === 'object' && 'default' in module) {
    return module.default;
  }
  // Fallback to module itself (for CommonJS-style or namespace exports)
  return module;
}

/**
 * Factory for core renderer and plugin loading
 */
class DefaultDependencyFactory {
  /**
   * Dynamically import the calendar renderer class
   * @returns {Promise<typeof CalendarRenderer>} The CalendarRenderer class
   * @throws {Error} With original error preserved in cause
   */
  static async createCalendarRenderer() {
    try {
      const module = await import('../core/CalendarRenderer.js');
      return extractDefaultExport(module);
    } catch (originalError) {
      errorHandler.handleFatalError(
        'Failed to import CalendarRenderer',
        originalError,
        { category: 'configuration', action: 'calendar-renderer-import-failed' }
      );
      // IMPROVED: Preserve original error via cause for debuggability
      throw new Error(`Failed to load CalendarRenderer: ${originalError.message}`, {
        cause: originalError
      });
    }
  }

  /**
   * Dynamically import FullCalendar plugins
   * @returns {Promise<Array>} Array of loaded plugins
   * @throws {Error} With original error preserved in cause
   */
  static async loadPlugins() {
    try {
      const module = await import('@fullcalendar/daygrid');
      return [extractDefaultExport(module)];
    } catch (originalError) {
      errorHandler.handleFatalError(
        'Failed to import FullCalendar plugins',
        originalError,
        { category: 'configuration', action: 'plugins-import-failed' }
      );
      // IMPROVED: Preserve original error via cause for debuggability
      throw new Error(`Failed to load FullCalendar plugins: ${originalError.message}`, {
        cause: originalError
      });
    }
  }
}

/**
 * Manages plugin cache and retrieval
 */
class PluginManager {
  constructor(dependencyFactory = DefaultDependencyFactory) {
    this.dependencyFactory = dependencyFactory;
    this.loadedPlugins = null;

    if (!dependencyFactory) {
      errorHandler.handleError(
        'PluginManager initialized without dependency factory',
        null,
        { category: 'initialization', action: 'missing-dependency-factory' }
      );
    }
  }

  /**
   * Get or load plugins via factory
   * @returns {Promise<Array>} Array of loaded plugins
   * @throws {Error} With original error preserved in cause
   */
  async getPlugins() {
    if (this.loadedPlugins) return this.loadedPlugins;
    try {
      this.loadedPlugins = await this.dependencyFactory.loadPlugins();
      return this.loadedPlugins;
    } catch (originalError) {
      errorHandler.handleError(
        'Failed to load plugins via factory',
        originalError,
        { category: 'configuration', action: 'plugin-loading-failed' }
      );
      // IMPROVED: Preserve original error chain - don't double-wrap if already wrapped
      if (originalError.cause) {
        // Error already has a cause chain, rethrow as-is
        throw originalError;
      }
      throw new Error(`Failed to load FullCalendar plugins: ${originalError.message}`, {
        cause: originalError
      });
    }
  }

  /**
   * Clear cached plugins
   */
  clearCache() {
    this.loadedPlugins = null;
  }
}

/**
 * Facade for renderer and plugin dependencies
 */
class RendererDependencyLoader {
  constructor({ dependencyFactory, pluginManager, CalendarRenderer } = {}) {
    this.dependencyFactory = dependencyFactory || DefaultDependencyFactory;
    this.pluginManager = pluginManager || new PluginManager(this.dependencyFactory);
    this.CalendarRenderer = CalendarRenderer || null;

    errorHandler.logSuccess(
      'RendererDependencyLoader initialized',
      { hasCustomFactory: !!dependencyFactory, hasCustomPluginManager: !!pluginManager }
    );
  }

  /**
   * Get CalendarRenderer class, loading if necessary
   * @returns {Promise<typeof CalendarRenderer>} The CalendarRenderer class
   * @throws {Error} With original error preserved in cause
   */
  async getCalendarRenderer() {
    if (this.CalendarRenderer) return this.CalendarRenderer;
    try {
      this.CalendarRenderer = await this.dependencyFactory.createCalendarRenderer();
      return this.CalendarRenderer;
    } catch (originalError) {
      errorHandler.handleError(
        'Failed to load CalendarRenderer via factory',
        originalError,
        { category: 'configuration', action: 'renderer-loading-failed' }
      );
      // IMPROVED: Preserve original error chain - don't double-wrap if already wrapped
      if (originalError.cause) {
        throw originalError;
      }
      throw new Error(`Failed to load CalendarRenderer: ${originalError.message}`, {
        cause: originalError
      });
    }
  }

  /**
   * Get FullCalendar plugins
   * @returns {Promise<Array>} Array of loaded plugins
   * @throws {Error} Rethrows original error to preserve debuggability
   */
  async getPlugins() {
    try {
      return await this.pluginManager.getPlugins();
    } catch (originalError) {
      errorHandler.handleError(
        'Failed to load plugins via plugin manager',
        originalError,
        { category: 'configuration', action: 'plugin-manager-failed' }
      );
      // IMPROVED: Rethrow original error directly to preserve full error chain
      throw originalError;
    }
  }

  /**
   * Clear renderer and plugin caches
   */
  clearCache() {
    this.pluginManager.clearCache();
    this.CalendarRenderer = null;
  }

  /**
   * Retrieve recent errors
   */
  getRecentErrors(minutes = 5) {
    return errorHandler.getRecentErrors(minutes);
  }

  /**
   * Check for critical loading issues
   */
  hasCriticalIssues() {
    return errorHandler.hasCriticalIssues();
  }

  /**
   * Reset error tracking history
   */
  clearErrorHistory() {
    errorHandler.clearErrorHistory();
  }

  /**
   * Get stats including error breakdown
   */
  getStats() {
    const stats = errorHandler.getErrorStats();
    return {
      hasDependencyFactory: !!this.dependencyFactory,
      hasPluginManager: !!this.pluginManager,
      hasLoadedRenderer: !!this.CalendarRenderer,
      hasLoadedPlugins: !!(this.pluginManager && this.pluginManager.loadedPlugins),
      errors: stats
    };
  }
}

// Named exports for flexibility - FIXED: No duplicate exports
export { DefaultDependencyFactory, PluginManager, RendererDependencyLoader };
